import { MAX_IMAGE_UPLOAD_BYTES } from '@/common/domain/utils/assertSafeImageUrl';
import {
  formatImageOversizeMessage,
  type ImageUploadRules,
  resolveImageUploadRules
} from '@/templates/presentation/utils/imageUploadRules';

const DEFAULT_ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg'] as const;

export { MAX_IMAGE_UPLOAD_BYTES };

/** PNG/JPG only — react-pdf cannot render SVG. */
export function isAllowedRasterImageFile(file: File, rules?: Pick<ImageUploadRules, 'allowedMimeTypes'>): boolean {
  const allowedMimeTypes = rules?.allowedMimeTypes ?? DEFAULT_ALLOWED_MIME_TYPES;
  const allowedSet = new Set(allowedMimeTypes.map((mime) => mime.toLowerCase()));

  if (file.type && allowedSet.has(file.type.toLowerCase())) {
    return true;
  }

  const name = file.name.toLowerCase();
  if (allowedSet.has('image/png') && name.endsWith('.png')) {
    return true;
  }

  if ((allowedSet.has('image/jpeg') || allowedSet.has('image/jpg')) && (name.endsWith('.jpg') || name.endsWith('.jpeg'))) {
    return true;
  }

  return false;
}

export interface ReadImageFileOptions {
  uploadRules?: ImageUploadRules;
}

/** Reads a PNG/JPG file as a data-URI for PDF-fidelity embedding. */
export function readImageFileAsDataUri(file: File, options?: ReadImageFileOptions): Promise<string> {
  const rules = options?.uploadRules ?? resolveImageUploadRules();

  if (!isAllowedRasterImageFile(file, rules)) {
    return Promise.reject(new Error('Only PNG and JPG images are supported'));
  }

  if (file.size > rules.maxFileSizeBytes) {
    return Promise.reject(new Error(formatImageOversizeMessage(rules)));
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string' && reader.result.startsWith('data:')) {
        resolve(reader.result);
        return;
      }
      reject(new Error('Failed to read image as data-URI'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}
